import React, { useState } from 'react';
import Helmet from '../components/Helmet/Helmet';
import CommonSection from '../components/Ui/CommonSection';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import '../styles/lookbook.css';
import products from '../assets/data/products';

// High-resolution architectural photography
import tableCoffeeImg from '../assets/images/table-coffee-01.jpg';
import tableDiningImg from '../assets/images/table-dining-01.jpg';
import lightingFloorImg from '../assets/images/lighting-floor-01.jpg';
import bedroomImg from '../assets/images/bedroom.jpg';

const scenes = [
  {
    id: 'haussmann-salon',
    category: 'living',
    tag: 'Parisian Grand Living',
    title: 'The Haussmann Grand Salon',
    subtitle: 'Architectural proportions, natural morning light, and tactile bouclé seating in quiet dialogue with honed Carrara marble.',
    heroImg: tableCoffeeImg,
    swatches: ['#E6DFD5', '#C59B27', '#2C2B29', '#8C8275'],
    pieceIds: ['01', '07', '11'], // Westview Sofa, Sakarias Armchair, Aura Marble Coffee Table
  },
  {
    id: 'nordic-dining',
    category: 'dining',
    tag: 'Nordic Gathering Space',
    title: 'The Nordic Dining Pavilion',
    subtitle: 'Solid sustainable white oak, floating frosted glass spheres, and tactile earth-toned stoneware for enduring family rituals.',
    heroImg: tableDiningImg,
    swatches: ['#E2D4C0', '#92400E', '#44403C', '#F59E0B'],
    pieceIds: ['12', '17', '18'], // København Dining Table, Solstice Chandelier, Kanso Ceramic Vases
  },
  {
    id: 'brutalist-loft',
    category: 'living',
    tag: 'Contemporary Minimalist',
    title: 'The Brutalist Atelier Lounge',
    subtitle: 'Monolithic planes of limewash plaster softened by arched brass illumination, deep memory foam lounging, and chiseled travertine.',
    heroImg: lightingFloorImg,
    swatches: ['#D6D3D1', '#78716C', '#1E293B', '#D97706'],
    pieceIds: ['02', '15', '13'], // Bigelow Couch, Vesper Floor Lamp, Søren Console Table
  },
  {
    id: 'coastal-sanctuary',
    category: 'suites',
    tag: 'Warm Coastal Sanctuary',
    title: 'The Sunlit Coastal Suite',
    subtitle: 'Organic washed linens, cloud-soft bouclé, organic brass mirrors, and tactile ceramic lighting for an unhurried, peaceful retreat.',
    heroImg: bedroomImg,
    swatches: ['#FAF5EF', '#E7D7C1', '#B7997A', '#3E342B'],
    pieceIds: ['04', '16', '19'], // Fluffy Sheep Daybed, Lumina Ceramic Table Lamp, Arcadia Brass Mirror
  },
];

const Lookbook = () => {
  const [filter, setFilter] = useState('all');

  const filteredScenes = filter === 'all' 
    ? scenes 
    : scenes.filter((s) => s.category === filter);

  return (
    <Helmet title="Atelier Lookbook & Curated Spaces | L'HABILLEUR">
      <CommonSection title="Atelier Lookbook" />

      {/* Hero Intro */}
      <section className="lookbook__hero">
        <Container>
          <span className="about__section-badge">Curated Spatial Aesthetics</span>
          <h2 className="fw-bold mb-3" style={{ color: 'var(--primary-color)', fontSize: '2.4rem' }}>
            Inspiring Architectural Living
          </h2>
          <p className="text-muted mx-auto mb-4" style={{ maxWidth: '650px' }}>
            Step inside our signature curated interiors. Each space is an exploration of quiet proportions, noble materials, and harmonious living.
          </p>

          {/* Category Filter Tabs */}
          <div className="lookbook__filter-tabs">
            <button
              type="button"
              className={`lookbook__tab-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All Curated Spaces
            </button>
            <button
              type="button"
              className={`lookbook__tab-btn ${filter === 'living' ? 'active' : ''}`}
              onClick={() => setFilter('living')}
            >
              Salons & Living Lounges
            </button>
            <button
              type="button"
              className={`lookbook__tab-btn ${filter === 'dining' ? 'active' : ''}`}
              onClick={() => setFilter('dining')}
            >
              Dining Pavilions
            </button>
            <button
              type="button"
              className={`lookbook__tab-btn ${filter === 'suites' ? 'active' : ''}`}
              onClick={() => setFilter('suites')}
            >
              Private Suites
            </button>
          </div>
        </Container>
      </section>

      {/* Editorial Scenes Showcase */}
      <section className="py-5 bg-light">
        <Container>
          {filteredScenes.map((scene) => {
            // Find products featured in this scene
            const sceneProducts = scene.pieceIds
              .map((id) => products.find((p) => p.id === id))
              .filter(Boolean);

            return (
              <div key={scene.id} className="lookbook__scene-wrapper">
                {/* Visual Scene Header */}
                <div className="lookbook__scene-img-box">
                  <img src={scene.heroImg} alt={scene.title} />
                  <div className="lookbook__scene-overlay">
                    <div className="lookbook__scene-meta">
                      <span className="lookbook__scene-tag">{scene.tag}</span>
                      <h3 className="lookbook__scene-title">{scene.title}</h3>
                      <p className="lookbook__scene-subtitle mb-2">{scene.subtitle}</p>
                      
                      <div className="lookbook__palette-row">
                        <span className="lookbook__palette-label">Curated Tones:</span>
                        {scene.swatches.map((color, idx) => (
                          <span
                            key={idx}
                            className="lookbook__swatch"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shop The Look Piece Strip */}
                <div className="lookbook__pieces-bar">
                  <div className="lookbook__pieces-header">
                    <i className="ri-compass-3-line"></i>
                    <span>Shop The Curated Pieces In This Space</span>
                  </div>

                  <Row className="g-3">
                    {sceneProducts.map((piece) => (
                      <Col key={piece.id} lg="4" md="6">
                        <Link to={`/shop/${piece.id}`} className="lookbook__piece-card">
                          <img
                            src={piece.imgUrl}
                            alt={piece.productName}
                            className="lookbook__piece-img"
                          />
                          <div className="lookbook__piece-info">
                            <h6 className="lookbook__piece-name">{piece.productName}</h6>
                            <span className="lookbook__piece-price">${piece.price}</span>
                          </div>
                          <div className="lookbook__piece-arrow">
                            <i className="ri-arrow-right-line"></i>
                          </div>
                        </Link>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>
            );
          })}
        </Container>
      </section>

      {/* Consultation Banner */}
      <section className="py-5 bg-white border-top text-center">
        <Container className="py-3">
          <h3 className="fw-bold mb-3" style={{ color: 'var(--primary-color)' }}>
            Need Bespoke Spatial Planning for Your Home?
          </h3>
          <p className="text-muted mx-auto mb-4" style={{ maxWidth: '600px' }}>
            Our interior architecture team offers complimentary 3D spatial curation and custom tailoring for your exact room dimensions.
          </p>
          <Link to="/services" className="buy__btn">
            Explore Bespoke Interior Services &rarr;
          </Link>
        </Container>
      </section>
    </Helmet>
  );
};

export default Lookbook;
